import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Stack, FormControlLabel,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    IconButton,
    Tooltip,
    Modal,
    CircularProgress,
    Checkbox,
    Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";
import DeleteIcon from "@mui/icons-material/Delete";
import ListIcon from "@mui/icons-material/List";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import KeyIcon from "@mui/icons-material/Key";
import CloseIcon from "@mui/icons-material/Close";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import axios from "axios";

// Styled Paper
const StyledPaper = styled(Paper)(({ theme, mode, customColors }) => ({
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    backgroundColor:
        mode === "dark"
            ? "#2A2A3C"
            : mode === "custom"
                ? customColors?.background || "#f5f5f5"
                : "#f0f7ff",
    borderRadius: "8px",
    boxShadow:
        mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.6)"
            : "0 2px 8px rgba(0,0,0,0.15)",
}));

// Palette setup
const palette = {
    light: {
        headerBg: "#e3f2fd",
        headerText: "#37474f",
        rowHover: "#f5f5f5",
        text: "#333",
    },
    dark: {
        headerBg: "#2A2A3C",
        headerText: "#EDEDED",
        rowHover: "#383C54",
        text: "#EDEDED",
    },
    custom: (customColors) => ({
        headerBg: customColors?.cardBg || "#f5f5f5",
        headerText: customColors?.text || "#111111",
        rowHover: customColors?.primary || customColors?.primary || "#e8f4ff",
        text: customColors?.text || "#111111",
    }),
};

export default function RegisterUserPanel({
    mode,
    customColors,
    setSnackbar,
    isLoading,
    setIsLoading,
    currentUserRole,
    myPermissions,
    customRolesDropdown: customRolesDropdownProp,
}) {
    // ========== STATE ==========
    const [selectedRegisterType, setSelectedRegisterType] = useState("single");
    const [registerForm, setRegisterForm] = useState({
        email: "",
        first_name: "",
        last_name: "",
        role: "",
        student_class: "",
        // password: "",
        // confirm_password: "",
    });
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkErrorModalOpen, setBulkErrorModalOpen] = useState(false);
    const [bulkErrorMessage, setBulkErrorMessage] = useState("");
    // const [otpModalOpen, setOtpModalOpen] = useState(false);
    // const [otp, setOtp] = useState('');
    // const [timer, setTimer] = useState(60);
    // const [otpError, setOtpError] = useState('');
    // const [newUserEmail, setNewUserEmail] = useState('');
    const [skipExisting, setSkipExisting] = useState(false);
    const [customRolesDropdown, setCustomRolesDropdown] = useState(customRolesDropdownProp || []);
    const [studentClasses, setStudentClasses] = useState([]);

    const fileInputRef = useRef(null);
    const apiUrl = process.env.REACT_APP_BASE_URL;

    // ========== EFFECTS ==========
    // Fetch custom roles for dropdown
    useEffect(() => {
        const fetchCustomRolesForDropdown = async () => {
            setIsLoading(true);
            try {
                const token = sessionStorage.getItem("authToken");
                const response = await axios.get(`${apiUrl}/custom-roles/`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const mappedRoles = Array.isArray(response.data)
                    ? response.data.map((role) => ({
                        id: role.id,
                        name: role.name,
                        permissions: role.permissions || {},
                        created_by: role.created_by,
                    }))
                    : Array.isArray(response.data.roles)
                        ? response.data.roles.map((role) => ({
                            id: role.id,
                            name: role.name,
                            permissions: role.permissions || {},
                            created_by: role.created_by,
                        }))
                        : [];
                setCustomRolesDropdown(mappedRoles);
            } catch (error) {
                console.error("Failed to fetch custom roles for dropdown:", error);
                setSnackbar({
                    open: true,
                    message: error.response?.data?.error || error.response?.data?.message || "Failed to fetch custom roles. Please try again.",
                    severity: "error",
                });
                setCustomRolesDropdown([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchCustomRolesForDropdown();
    }, [setIsLoading, setSnackbar]);

        // Fetch student classes
    useEffect(() => {
        const fetchStudentClasses = async () => {
            try {
                const token = sessionStorage.getItem("authToken");
                const response = await axios.get(`${apiUrl}/admins/view-student-classes/`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                // Assuming API returns array of objects like [{ id: 1, name: "Class 10", ... }]
                const classes = Array.isArray(response.data) 
                    ? response.data 
                    : Array.isArray(response.data?.classes) 
                        ? response.data.classes 
                        : [];
                
                setStudentClasses(classes);
            } catch (error) {
                console.error("Failed to fetch student classes:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to load student classes. Please try again.",
                    severity: "error",
                });
                setStudentClasses([]);
            }
        };

        fetchStudentClasses();
    }, [apiUrl, setSnackbar]);

    // ========== HANDLERS ==========
    const handleDisabledAction = () => {
        setSnackbar({
            open: true,
            message: "You do not have permission to perform this action.",
            severity: "warning",
        });
    };

    const handleCopyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setSnackbar({
                open: true,
                message: `${text} copied to clipboard!`,
                severity: "success",
            });
        }).catch((err) => {
            console.error("Failed to copy:", err);
            setSnackbar({
                open: true,
                message: "Failed to copy to clipboard.",
                severity: "error",
            });
        });
    };

    const availableRoles = () => {
        return customRolesDropdown.map((role) => role.name);
    };

    const handleRegisterFormChange = (key) => (event) => {
        setRegisterForm((prev) => ({
            ...prev,
            [key]: event.target.value,
        }));
    };

    const handleRegisterTypeChange = (type) => () => {
        setSelectedRegisterType(type);
    };

    const handleExportTemplate = async () => {
        try {
            const token = sessionStorage.getItem("authToken");
            const response = await axios.get(`${apiUrl}/export-bulk-reg-template/`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: 'blob', // Important for downloading files
            });

            // Create a blob URL and trigger download
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'bulk_registration_template.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setSnackbar({
                open: true,
                message: "Template downloaded successfully!",
                severity: "success",
            });
        } catch (error) {
            console.error("Failed to export template:", error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || "Failed to export template. Please try again.",
                severity: "error",
            });
        }
    };

    const handleRegisterSubmit = async (event) => {
        event.preventDefault();

        try {
            const token = sessionStorage.getItem("authToken");
            const payload = {
                email: registerForm.email,
                first_name: registerForm.first_name,
                last_name: registerForm.last_name,
                role: registerForm.role,
                student_class: registerForm.student_class,
            };

            const isCustomRole = customRolesDropdown.some((role) => role.name === currentUserRole);
            const apiEndpoint = isCustomRole
                ? `${apiUrl}/custom-user/register-user/`
                : `${apiUrl}/admin-register-user/`;

            if (isCustomRole && registerForm.role === "admin") {
                if (!myPermissions.can_register_sub_admin) {
                    setSnackbar({
                        open: true,
                        message: "You do not have permission to register this role.",
                        severity: "warning",
                    });
                    return;
                }
            }

            const response = await axios.post(apiEndpoint, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // Success handling
            setSnackbar({
                open: true,
                message: response.data.message || "User registered successfully. An activation link has been sent to the email.",
                severity: "success",
            });

            // Reset form
            setRegisterForm({
                email: "",
                first_name: "",
                last_name: "",
                role: "",
                student_class: "",
            });
            
        } catch (error) {
            console.error("Failed to register user:", error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || error.response?.data?.email?.[0] || "Failed to register user. Please try again.",
                severity: "error",
            });
        }
    };

    const handleBulkFileChange = (event) => {
        setBulkFile(event.target.files[0]);
    };

    const handleBulkUpload = async () => {
        if (!bulkFile) {
            setSnackbar({
                open: true,
                message: "Please select a file.",
                severity: "warning",
            });
            return;
        }
        if (currentUserRole !== "admin" && !myPermissions.can_bulk_registration) {
            handleDisabledAction();
            return;
        }
        const formData = new FormData();
        formData.append("file", bulkFile);
        try {
            const token = sessionStorage.getItem("authToken");
            const response = await axios.post(`${apiUrl}/api/users/bulk-register/`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
            });
            const message = response.data.message || "Users registered successfully via bulk upload.";
            setSnackbar({
                open: true,
                message: message,
                severity: "success",
            });
            setBulkFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setBulkErrorModalOpen(false);
        } catch (error) {
            console.error("Failed to bulk register:", error);
            if (error.response?.data?.error && error.response.data.error.toLowerCase().includes("emails already exist")) {
                setBulkErrorMessage(error.response.data.error);
                setBulkErrorModalOpen(true);
                return;
            }
            setSnackbar({
                open: true,
                message: error.response?.data?.message || error.response?.data?.error || "Failed to bulk register users. Please try again.",
                severity: "error",
            });
        }
    };

    const handleSkipAndRegister = async () => {
        if (!bulkFile) {
            setSnackbar({
                open: true,
                message: "No file selected for bulk registration.",
                severity: "warning",
            });
            return;
        }
        if (currentUserRole !== "admin" && !myPermissions.can_bulk_registration) {
            handleDisabledAction();
            return;
        }
        const formData = new FormData();
        formData.append("file", bulkFile);
        try {
            const token = sessionStorage.getItem("authToken");
            const response = await axios.post(`${apiUrl}/api/users/bulk-register/?skip_existing=true`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
            });
            const message = response.data.message || "Users registered successfully, skipping existing emails.";
            setSnackbar({
                open: true,
                message: message,
                severity: "success",
            });
            setBulkFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setBulkErrorModalOpen(false);
        } catch (error) {
            console.error("Failed to bulk register with skip:", error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || "Failed to bulk register users with skip. Please try again.",
                severity: "error",
            });
        }
    };

    const handleCloseBulkErrorModal = () => {
        setBulkErrorModalOpen(false);
        setBulkErrorMessage("");
    };

    // const handleOtpChange = (event) => {
    //     const value = event.target.value.replace(/\D/g, '').slice(0, 6);
    //     setOtp(value);
    // };

    // const handleOtpSubmit = async () => {
    //     if (otp.length !== 6) {
    //         setOtpError("Please enter a 6-digit OTP.");
    //         return;
    //     }
    //     try {
    //         const token = sessionStorage.getItem("authToken");
    //         await axios.post(`${apiUrl}/verify/`,
    //             { otp: otp },
    //         );
    //         setOtpModalOpen(false);
    //         setOtp("");
    //         setTimer(60);
    //         setOtpError("");
    //         setNewUserEmail("");
    //         setSnackbar({
    //             open: true,
    //             message: "Email verified successfully!",
    //             severity: "success",
    //         });
    //     } catch (error) {
    //         setOtpError(error.response?.data?.message || "Invalid OTP. Please try again.");
    //     }
    // };

    // const handleOtpResend = async (email) => {
    //     const emailToUse = email || newUserEmail;
    //     if (!emailToUse) {
    //         setSnackbar({
    //             open: true,
    //             message: "Email not available for OTP resend.",
    //             severity: "error",
    //         });
    //         return;
    //     }
    //     try {
    //         const token = sessionStorage.getItem("authToken");
    //         await axios.post(`${apiUrl}/resend-otp/`,
    //             { email: emailToUse },
    //         );
    //         setTimer(60);
    //         setOtp('');
    //         setOtpError('');
    //         setSnackbar({
    //             open: true,
    //             message: "OTP resent successfully!",
    //             severity: "success",
    //         });
    //     } catch (error) {
    //         console.error("Failed to resend OTP:", error);
    //         setSnackbar({
    //             open: true,
    //             message: error.response?.data?.message || "Failed to resend OTP. Please try again.",
    //             severity: "error",
    //         });
    //     }
    // };

    // const handleOtpModalClose = () => {
    //     setOtpModalOpen(false);
    //     setOtp('');
    //     setTimer(60);
    //     setOtpError('');
    //     setNewUserEmail('');
    // };

    // const handleOpenOtpModal = async (email) => {
    //     setNewUserEmail(email);
    //     setOtp("");
    //     setTimer(60);
    //     setOtpError("");
    //     setOtpModalOpen(true);
    //     await handleOtpResend(email);
    // };

    // ========== RENDER ==========
    return (
        <>
            <StyledPaper
                sx={{
                    backgroundColor: mode === "dark"
                        ? palette.dark.headerBg
                        : mode === "custom"
                            ? customColors?.bgNav || "#f5f5f5"
                            : palette.light.headerBg,
                    width: "80%",
                    justifySelf: "center",
                    borderRadius: "16px",
                    boxShadow: mode === "dark"
                        ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                        : "0 8px 24px rgba(30, 139, 175, 0.12)",
                    p: 3,
                }}
            >
                {/* Header: Title + Toggle Buttons */}
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        mb: 3,
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        pb: 2,
                        borderBottom: mode === "dark"
                            ? "2px solid rgba(255, 255, 255, 0.1)"
                            : "2px solid rgba(30, 139, 175, 0.15)",
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                            fontWeight: 700,
                            letterSpacing: "-0.5px",
                        }}
                    >
                        {selectedRegisterType === "single" ? "Register Single User" : "Register Multiple Users"}
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                        {[
                            { key: "single", label: "Single User" },
                            { key: "bulk", label: "Multiple Users" },
                        ].map((item) => (
                            <Button
                                key={item.key}
                                variant={selectedRegisterType === item.key ? "contained" : "outlined"}
                                onClick={handleRegisterTypeChange(item.key)}
                                sx={{
                                    '&:focus': { outline: 'none' },
                                    borderRadius: 3,
                                    background: selectedRegisterType === item.key
                                        ? "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)"
                                        : "transparent",
                                    color: selectedRegisterType === item.key
                                        ? "white"
                                        : mode === "dark"
                                            ? palette.dark.headerText
                                            : mode === "custom"
                                                ? customColors?.text || "#111111"
                                                : "#1e3a8a",
                                    borderColor: selectedRegisterType === item.key
                                        ? "transparent"
                                        : mode === "dark"
                                            ? "rgba(255, 255, 255, 0.3)"
                                            : "rgba(30, 139, 175, 0.3)",
                                    borderWidth: "2px",
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1,
                                    textTransform: "none",
                                    boxShadow: selectedRegisterType === item.key
                                        ? "0 4px 12px rgba(30, 58, 138, 0.3)"
                                        : "none",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        background: selectedRegisterType === item.key
                                            ? "linear-gradient(135deg, #1a3278 0%, #1a7a9a 100%)"
                                            : mode === "dark"
                                                ? "rgba(30, 139, 175, 0.1)"
                                                : "rgba(30, 139, 175, 0.08)",
                                        borderColor: selectedRegisterType === item.key
                                            ? "transparent"
                                            : "rgba(30, 139, 175, 0.5)",
                                        transform: "translateY(-2px)",
                                        boxShadow: selectedRegisterType === item.key
                                            ? "0 6px 16px rgba(30, 58, 138, 0.4)"
                                            : "0 4px 12px rgba(30, 139, 175, 0.2)",
                                    },
                                }}
                            >
                                {item.label}
                            </Button>
                        ))}
                    </Stack>
                </Stack>

                {/* Single User Registration Form */}
                {selectedRegisterType === "single" && (
                    <Box
                        component="form"
                        onSubmit={handleRegisterSubmit}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                            mt: 1,
                        }}
                    >
                        <TextField
                            label="Email"
                            type="email"
                            value={registerForm.email}
                            onChange={handleRegisterFormChange("email")}
                            required
                            fullWidth
                            error={
                                !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(registerForm.email) &&
                                registerForm.email !== ""
                            }
                            helperText={
                                !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(registerForm.email) &&
                                    registerForm.email !== ""
                                    ? "Please enter a valid email address"
                                    : ""
                            }
                            sx={{
                                "& .MuiInputBase-root": {
                                    backgroundColor: mode === "dark"
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : mode === "custom"
                                            ? customColors?.inputBg || "#f0f7ff"
                                            : "#f0f7ff",
                                    borderRadius: "12px",
                                    "&:hover": {
                                        backgroundColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.08)"
                                            : mode === "custom"
                                                ? customColors?.inputBgHover || "#e0f0ff"
                                                : "#e0f0ff",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: mode === "dark"
                                        ? palette.dark.headerText
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.2)"
                                            : "rgba(30, 139, 175, 0.3)",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#1e8bafff",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#1e8bafff",
                                        borderWidth: "2px",
                                    },
                                },
                            }}
                        />
                        <TextField
                            label="First Name"
                            value={registerForm.first_name}
                            onChange={handleRegisterFormChange("first_name")}
                            required
                            fullWidth
                            sx={{
                                "& .MuiInputBase-root": {
                                    backgroundColor: mode === "dark"
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : mode === "custom"
                                            ? customColors?.inputBg || "#f0f7ff"
                                            : "#f0f7ff",
                                    borderRadius: "12px",
                                    "&:hover": {
                                        backgroundColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.08)"
                                            : mode === "custom"
                                                ? customColors?.inputBgHover || "#e0f0ff"
                                                : "#e0f0ff",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: mode === "dark"
                                        ? palette.dark.headerText
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.2)"
                                            : "rgba(30, 139, 175, 0.3)",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#1e8bafff",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#1e8bafff",
                                        borderWidth: "2px",
                                    },
                                },
                            }}
                        />
                        <TextField
                            label="Last Name"
                            value={registerForm.last_name}
                            onChange={handleRegisterFormChange("last_name")}
                            required
                            fullWidth
                            sx={{
                                "& .MuiInputBase-root": {
                                    backgroundColor: mode === "dark"
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : mode === "custom"
                                            ? customColors?.inputBg || "#f0f7ff"
                                            : "#f0f7ff",
                                    borderRadius: "12px",
                                    "&:hover": {
                                        backgroundColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.08)"
                                            : mode === "custom"
                                                ? customColors?.inputBgHover || "#e0f0ff"
                                                : "#e0f0ff",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: mode === "dark"
                                        ? palette.dark.headerText
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.2)"
                                            : "rgba(30, 139, 175, 0.3)",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#1e8bafff",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#1e8bafff",
                                        borderWidth: "2px",
                                    },
                                },
                            }}
                        />
                        <FormControl fullWidth>
                            <InputLabel
                                id="role-select-label"
                                sx={{
                                    color: mode === "dark"
                                        ? palette.dark.headerText
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                }}
                            >
                                Role
                            </InputLabel>
                            <Select
                                labelId="role-select-label"
                                value={registerForm.role}
                                label="Role"
                                onChange={handleRegisterFormChange("role")}
                                required
                                sx={{
                                    backgroundColor: mode === "dark"
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : mode === "custom"
                                            ? customColors?.inputBg || "#f0f7ff"
                                            : "#f0f7ff",
                                    borderRadius: "12px",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.2)"
                                            : "rgba(30, 139, 175, 0.3)",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#1e8bafff",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#1e8bafff",
                                        borderWidth: "2px",
                                    },
                                }}
                            >
                                {isLoading ? (
                                    <MenuItem value="" disabled>
                                        Loading roles...
                                    </MenuItem>
                                ) : availableRoles().length > 0 ? (
                                    availableRoles().map((role) => (
                                        <MenuItem key={role} value={role}>
                                            {role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem value="" disabled>
                                        No roles available
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        {/* <TextField
                            label="Password"
                            type="password"
                            value={registerForm.password}
                            onChange={handleRegisterFormChange("password")}
                            required
                            fullWidth
                            sx={{
                                "& .MuiInputBase-root": {
                                    backgroundColor: mode === "dark"
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : mode === "custom"
                                            ? customColors?.inputBg || "#f0f7ff"
                                            : "#f0f7ff",
                                    borderRadius: "12px",
                                    "&:hover": {
                                        backgroundColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.08)"
                                            : mode === "custom"
                                                ? customColors?.inputBgHover || "#e0f0ff"
                                                : "#e0f0ff",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: mode === "dark"
                                        ? palette.dark.headerText
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.2)"
                                            : "rgba(30, 139, 175, 0.3)",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#1e8bafff",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#1e8bafff",
                                        borderWidth: "2px",
                                    },
                                },
                            }}
                        />
                        {/* <TextField
                            label="Confirm Password"
                            type="password"
                            value={registerForm.confirm_password}
                            onChange={handleRegisterFormChange("confirm_password")}
                            required
                            fullWidth
                            sx={{
                                "& .MuiInputBase-root": {
                                    backgroundColor: mode === "dark"
                                        ? "rgba(255, 255, 255, 0.05)"
                                        : mode === "custom"
                                            ? customColors?.inputBg || "#f0f7ff"
                                            : "#f0f7ff",
                                    borderRadius: "12px",
                                    "&:hover": {
                                        backgroundColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.08)"
                                            : mode === "custom"
                                                ? customColors?.inputBgHover || "#e0f0ff"
                                                : "#e0f0ff",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: mode === "dark"
                                        ? palette.dark.headerText
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                },
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.2)"
                                            : "rgba(30, 139, 175, 0.3)",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#1e8bafff",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#1e8bafff",
                                        borderWidth: "2px",
                                    },
                                },
                            }}
                        /> */}

                        {/*  Class Field - Shown only for student-related roles */}
                        {(registerForm.role.toLowerCase().includes("student") || 
                          registerForm.role.toLowerCase() === "student" || 
                          registerForm.role.toLowerCase() === "paid_student") && (
                            <FormControl fullWidth>
                                <InputLabel id="class-select-label">Class</InputLabel>
                                <Select
                                    labelId="class-select-label"
                                    value={registerForm.student_class}
                                    label="Class"
                                    onChange={handleRegisterFormChange("student_class")}
                                    required
                                    sx={{
                                        backgroundColor: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.05)"
                                            : mode === "custom"
                                                ? customColors?.inputBg || "#f0f7ff"
                                                : "#f0f7ff",
                                        borderRadius: "12px",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: mode === "dark"
                                                ? "rgba(255, 255, 255, 0.2)"
                                                : "rgba(30, 139, 175, 0.3)",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#1e8bafff",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#1e8bafff",
                                            borderWidth: "2px",
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>Select Class</MenuItem>
                                    {studentClasses.map((cls) => (
                                        <MenuItem key={cls.id || cls.name} value={cls.name || cls.class_name || cls.id}>
                                            {cls.name || cls.class_name || `Class ${cls.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(registerForm.email)}
                            sx={{
                                background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "1rem",
                                textTransform: "none",
                                py: 1.5,
                                borderRadius: "12px",
                                boxShadow: "0 4px 14px rgba(30, 139, 175, 0.3)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    background: "linear-gradient(135deg, #1e3a8a 0%, #1a7a9a 100%)",
                                    boxShadow: "0 6px 20px rgba(30, 139, 175, 0.4)",
                                    transform: "translateY(-2px)",
                                },
                                "&:active": {
                                    transform: "translateY(0px)",
                                    boxShadow: "0 2px 8px rgba(30, 139, 175, 0.3)",
                                },
                                "&.Mui-disabled": {
                                    background: "linear-gradient(135deg, #9ca3af 0%, #cbd5e1 100%)",
                                    color: "#ffffffcc",
                                    boxShadow: "none",
                                    opacity: 0.7,
                                    cursor: "not-allowed",
                                    transform: "none",
                                },
                            }}
                        >
                            Register
                        </Button>
                    </Box>
                )}

                {/* Bulk User Registration */}
                {selectedRegisterType === "bulk" && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 2,
                                color: mode === "dark"
                                    ? palette.dark.text
                                    : mode === "custom"
                                        ? customColors?.text || "#111111"
                                        : "#37474f",
                            }}
                        >
                            To register multiple users, prepare a CSV file with the following columns:
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 2,
                                mb: 2,
                                backgroundColor: mode === "dark"
                                    ? "#1E1E2E"
                                    : mode === "custom"
                                        ? customColors?.cardBg || "#d0d0d0"
                                        : "#e6e6e6",
                                p: 2,
                                borderRadius: "8px",
                                border: mode === "dark"
                                    ? "1px solid rgba(255, 255, 255, 0.1)"
                                    : "1px solid rgba(30, 139, 175, 0.15)",
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    color: mode === "dark"
                                        ? palette.dark.text
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                    mr: 3,
                                }}
                            >
                                email
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: mode === "dark"
                                        ? palette.dark.text
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                    mr: 3,
                                }}
                            >
                                first_name
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: mode === "dark"
                                        ? palette.dark.text
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                    mr: 3,
                                }}
                            >
                                last_name
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: mode === "dark"
                                        ? palette.dark.text
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                    mr: 3,
                                }}
                            >
                                Role
                            </Typography>
                            {/* <Typography
                                variant="body1"
                                sx={{
                                    color: mode === "dark"
                                        ? palette.dark.text
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                     mr: 3,
                                }}
                            >
                                password
                            </Typography> */}
                            <Typography
                                variant="body1"
                                sx={{
                                    color: mode === "dark"
                                        ? palette.dark.text
                                        : mode === "custom"
                                            ? customColors?.text || "#111111"
                                            : "#1e3a8a",
                                    fontWeight: 500,
                                }}
                            >
                                 student_class
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleCopyToClipboard("email\tfirst_name\tlast_name\trole")}
                                >
                                    <CopyAllIcon
                                        fontSize="small"
                                        sx={{
                                            color: mode === "dark"
                                                ? palette.dark.text
                                                : mode === "custom"
                                                    ? customColors?.text || "#111111"
                                                    : "#1e3a8a",
                                        }}
                                    />
                                </IconButton>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: mode === "dark"
                                            ? "#BDBDBD"
                                            : mode === "custom"
                                                ? customColors?.text
                                                    ? `${customColors.text}80`
                                                    : "#757575"
                                                : "#757575",
                                        ml: 0.5,
                                    }}
                                >
                                    copy
                                </Typography>
                            </Box>
                        </Box>
                        <ul
                            style={{
                                marginBottom: "24px",
                                paddingLeft: "16px",
                                color: mode === "dark"
                                    ? palette.dark.text
                                    : mode === "custom"
                                        ? customColors?.text || "#111111"
                                        : "#37474f",
                            }}
                        >
                            <li><strong>email</strong>: User's Gmail address (e.g., user@gmail.com)</li>
                            <li><strong>first_name</strong>: User's first name</li>
                            <li><strong>last_name</strong>: User's last name</li>
                            <li><strong>role</strong>: User's role</li>
                            {/* <li><strong>password</strong>: User's password</li> */}
                            <li><strong>student_class</strong>: Class for both student and paid student</li>
                        </ul>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2,
                                color: mode === "dark"
                                    ? "rgba(255, 255, 255, 0.5)"
                                    : "rgba(0, 0, 0, 0.5)",
                            }}
                        >
                            Note: Each row represents one user. Ensure emails are unique and valid Gmail addresses. Roles will be assigned based on your permissions.
                        </Typography>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleBulkFileChange}
                            style={{ display: "none" }}
                        />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
                            {bulkFile ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: mode === "dark"
                                                ? palette.dark.text
                                                : mode === "custom"
                                                    ? customColors?.text || "#111111"
                                                    : "#37474f",
                                        }}
                                    >
                                        Selected: {bulkFile.name}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        onClick={() => fileInputRef.current?.click()}
                                        size="small"
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: "8px",
                                            borderColor: mode === "dark"
                                                ? "rgba(255, 255, 255, 0.3)"
                                                : "rgba(30, 139, 175, 0.3)",
                                            color: mode === "dark"
                                                ? palette.dark.headerText
                                                : "#1e3a8a",
                                        }}
                                    >
                                        Change
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleExportTemplate}
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: "12px",
                                            borderColor: mode === "dark"
                                                ? "rgba(255, 255, 255, 0.3)"
                                                : "rgba(30, 139, 175, 0.3)",
                                            color: mode === "dark"
                                                ? palette.dark.headerText
                                                : "#1e3a8a",
                                            px: 3,
                                            py: 1.5,
                                            fontWeight: 600,
                                            "&:hover": {
                                                backgroundColor: mode === "dark"
                                                    ? "rgba(30, 139, 175, 0.1)"
                                                    : "rgba(30, 139, 175, 0.08)",
                                                borderColor: "rgba(30, 139, 175, 0.5)",
                                            },
                                        }}
                                    >
                                        Export Template
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: "12px",
                                            borderColor: mode === "dark"
                                                ? "rgba(255, 255, 255, 0.3)"
                                                : "rgba(30, 139, 175, 0.3)",
                                            color: mode === "dark"
                                                ? palette.dark.headerText
                                                : "#1e3a8a",
                                            px: 3,
                                            py: 1.5,
                                            fontWeight: 600,
                                            "&:hover": {
                                                backgroundColor: mode === "dark"
                                                    ? "rgba(30, 139, 175, 0.1)"
                                                    : "rgba(30, 139, 175, 0.08)",
                                                borderColor: "rgba(30, 139, 175, 0.5)",
                                            },
                                        }}
                                    >
                                        Choose CSV File
                                    </Button>
                                    
                                </Box>
                            )}
                            {bulkFile && (
                                <Button
                                    variant="contained"
                                    onClick={handleBulkUpload}
                                    sx={{
                                        backgroundColor: "#4caf50",
                                        color: "white",
                                        fontWeight: 600,
                                        textTransform: "none",
                                        borderRadius: "12px",
                                        boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            backgroundColor: "#388e3c",
                                            boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
                                            transform: "translateY(-2px)",
                                        },
                                        px: 3,
                                        py: 1.5,
                                    }}
                                >
                                    Upload and Register
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </StyledPaper>

            {/* Bulk Error Modal */}
            {bulkErrorModalOpen && (
                <Modal
                    open={bulkErrorModalOpen}
                    onClose={handleCloseBulkErrorModal}
                    aria-labelledby="bulk-error-modal-title"
                    aria-describedby="bulk-error-modal-description"
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 400,
                            bgcolor: "background.paper",
                            borderRadius: "8px",
                            boxShadow: 24,
                            p: 4,
                        }}
                    >
                        <Typography
                            id="bulk-error-modal-title"
                            variant="h6"
                            sx={{
                                color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                mb: 2,
                                fontWeight: "500",
                            }}
                        >
                            Existing Emails Found
                        </Typography>
                        <Typography
                            id="bulk-error-modal-description"
                            sx={{
                                mb: 2,
                                color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.text || "#111111" : palette.light.text,
                            }}
                        >
                            {bulkErrorMessage}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={skipExisting}
                                    onChange={(e) => setSkipExisting(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Skip existing emails"
                            sx={{
                                mt: 2,
                                color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.text || "#111111" : palette.light.text,
                            }}
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleCloseBulkErrorModal}
                                sx={{
                                    color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                    borderColor: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                    textTransform: "none",
                                    borderRadius: "8px",
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    if (!skipExisting) {
                                        setSnackbar({
                                            open: true,
                                            message: "Please check 'Skip existing emails' to proceed.",
                                            severity: "warning",
                                        });
                                        return;
                                    }
                                    handleCloseBulkErrorModal();
                                    handleSkipAndRegister();
                                }}
                                disabled={!skipExisting}
                                sx={{
                                    backgroundColor: "#4caf50",
                                    color: "white",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    "&:hover": { backgroundColor: "#388e3c" },
                                    "&.Mui-disabled": { backgroundColor: "#a5d6a7", color: "white" },
                                }}
                            >
                                Upload
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            )}

            {/* OTP Modal */}
            {/* <Modal
                open={otpModalOpen}
                aria-labelledby="otp-modal-title"
                aria-describedby="otp-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        borderRadius: "8px",
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography
                            id="otp-modal-title"
                            variant="h6"
                            sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: "500" }}
                        >
                            Verify Email
                        </Typography>
                        <IconButton onClick={handleOtpModalClose}>
                            <CloseIcon sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText }} />
                        </IconButton>
                    </Box>
                    <Typography sx={{ mb: 2, color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.primary || "#111111" : palette.light.text }}>
                        Enter the 6-digit OTP sent to {newUserEmail}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            value={otp}
                            onChange={handleOtpChange}
                            placeholder="Enter OTP"
                            inputProps={{ maxLength: 6 }}
                            fullWidth
                            sx={{
                                "& .MuiInputBase-root": { backgroundColor: mode === "dark" ? palette.dark.inputBg : mode === "custom" ? customColors?.background || "#f5f5f5" : "#f0f7ff", borderRadius: "8px" },
                                "& .MuiInputLabel-root": { color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: "500" },
                            }}
                        />
                    </Box>
                    {otpError && (
                        <Typography sx={{ color: "#d32f2f", mb: 2 }}>
                            {otpError}
                        </Typography>
                    )}
                    <Typography sx={{ mb: 2, color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.primary || "#111111" : palette.light.text }}>
                        Time remaining: {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Button
                            variant="text"
                            onClick={() => handleOtpResend()}
                            disabled={timer > 0}
                            sx={{ color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.primary || "#111111" : "#142be1", textTransform: "none" }}
                        >
                            Resend OTP
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleOtpSubmit}
                            disabled={otp.length !== 6}
                            sx={{
                                backgroundColor: "#4caf50",
                                color: "white",
                                textTransform: "none",
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "#388e3c" },
                            }}
                        >
                            Verify
                        </Button>
                    </Box>
                </Box>
            </Modal> */}
        </>
    );
}
